//@ts-nocheck
import React, { useState } from 'react';
import { observer } from 'mobx-react';
import Button from '../bootstrap/Button';
import Modal, {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '../bootstrap/Modal';
import Card, { CardBody, CardHeader, CardTitle } from '../bootstrap/Card';
import Icon from '../icon/Icon';
import auth from '../../shared/mobx/auth-state';
import notificationState from '../../shared/mobx/notification-state';
import settingsState from '../../shared/mobx/settings-state';
import { usePaddle } from '../../hooks/usePaddle';

interface PaymentMethodManagerProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const PaymentMethodManager: React.FC<PaymentMethodManagerProps> = ({ isOpen, setIsOpen }) => {
  const [loading, setLoading] = useState(false);

  const paddle = usePaddle(
    {
      environment: settingsState.paddleEnviroment?.environment || 'sandbox',
      token: settingsState.paddleEnviroment?.token || ''
    },
    () => {
      // On successful portal action
      notificationState.addToasterSuccess('Changes saved successfully!');
      setLoading(false);
      setIsOpen(false);
    },
    () => {
      // On error
      notificationState.addToasterError('Failed to open customer portal');
      setLoading(false);
    }
  );

  const openCustomerPortal = () => {
    console.log('Customer Portal is not available in this Paddle version');
    notificationState.addToasterError('Customer Portal is not available in your current Paddle version. Please use the "Manage Subscription" button instead.');
    setLoading(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title="Manage Subscription"
      size="lg"
      isCentered
    >
      <ModalHeader setIsOpen={setIsOpen}>
        <ModalTitle>Customer Portal</ModalTitle>
      </ModalHeader>
      
      <ModalBody>
        <div className="row">
          <div className="col-12">
            <Card>
              <CardHeader>
                <CardTitle>Manage Your Subscription</CardTitle>
              </CardHeader>
              <CardBody>
                <div className="text-center py-4">
                  <Icon icon="CreditCard" size="3x" className="text-primary mb-3" />
                  <h5 className="mb-3">Customer Portal</h5>
                  <p className="text-muted mb-4">
                    Access your secure customer portal to manage your subscription, 
                    update payment methods, view invoices, and more.
                  </p>
                  
                  <div className="row text-start">
                    <div className="col-md-6">
                      <h6 className="text-primary">What you can do:</h6>
                      <ul className="list-unstyled">
                        <li><Icon icon="Check" className="text-success me-2" />Update payment method</li>
                        <li><Icon icon="Check" className="text-success me-2" />Change billing details</li>
                        <li><Icon icon="Check" className="text-success me-2" />View invoices</li>
                        <li><Icon icon="Check" className="text-success me-2" />Manage subscription</li>
                        <li><Icon icon="Check" className="text-success me-2" />Cancel subscription</li>
                      </ul>
                    </div>
                    <div className="col-md-6">
                      <h6 className="text-primary">Security features:</h6>
                      <ul className="list-unstyled">
                        <li><Icon icon="Shield" className="text-info me-2" />PCI compliant</li>
                        <li><Icon icon="Shield" className="text-info me-2" />Secure by default</li>
                        <li><Icon icon="Shield" className="text-info me-2" />Encrypted data</li>
                        <li><Icon icon="Shield" className="text-info me-2" />Regular updates</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      </ModalBody>
      
      <ModalFooter>
        <Button
          color="secondary"
          isOutline
          onClick={() => setIsOpen(false)}
          disabled={loading}
        >
          Close
        </Button>
        
        <Button
          color="primary"
          onClick={openCustomerPortal}
          disabled={loading || !auth.currentUser.customer_id || !paddle}
        >
          {loading ? (
            <>
              <Icon icon="Refresh" spin className="me-2" />
              Opening Portal...
            </>
          ) : !paddle ? (
            <>
              <Icon icon="Clock" className="me-2" />
              Loading Payment System...
            </>
          ) : !auth.currentUser.customer_id ? (
            <>
              <Icon icon="ExclamationTriangle" className="me-2" />
              Customer ID Required
            </>
          ) : (
            <>
              <Icon icon="ExternalLink" className="me-2" />
              Open Customer Portal
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default observer(PaymentMethodManager);
